import React from "react";
import Markdown from "markdown-to-jsx";
import { parsePhoneNumbers } from "../../utils/phoneLinkParser";

const elements = [
  "a",
  "abbr",
  "address",
  "area",
  "article",
  "aside",
  "audio",
  "b",
  "base",
  "bdi",
  "bdo",
  "big",
  "blockquote",
  "body",
  "br",
  "button",
  "canvas",
  "caption",
  "cite",
  "code",
  "col",
  "colgroup",
  "data",
  "datalist",
  "dd",
  "del",
  "details",
  "dfn",
  "dialog",
  "div",
  "dl",
  "dt",
  "em",
  "embed",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "head",
  "header",
  "hgroup",
  "hr",
  "html",
  "i",
  "iframe",
  "img",
  "input",
  "ins",
  "kbd",
  "keygen",
  "label",
  "legend",
  "li",
  "link",
  "main",
  "map",
  "mark",
  "marquee",
  "menu",
  "menuitem",
  "meta",
  "meter",
  "nav",
  "noscript",
  "object",
  "ol",
  "optgroup",
  "option",
  "output",
  "p",
  "param",
  "picture",
  "pre",
  "progress",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "script",
  "section",
  "select",
  "small",
  "source",
  "span",
  "strong",
  "style",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "textarea",
  "tfoot",
  "th",
  "thead",
  "time",
  "title",
  "tr",
  "track",
  "u",
  "ul",
  "var",
  "video",
  "wbr",

  // SVG
  "circle",
  "clipPath",
  "defs",
  "ellipse",
  "foreignObject",
  "g",
  "image",
  "line",
  "linearGradient",
  "marker",
  "mask",
  "path",
  "pattern",
  "polygon",
  "polyline",
  "radialGradient",
  "rect",
  "stop",
  "svg",
  "text",
  "tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

const CustomLink = ({ children, ...props }) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const MarkdownWrapper = ({ children, onPhoneClick }) => {
  const boldRegex = /\*(.*?)\*/g;
  const tildaRegex = /~(.*?)~/g;

  const pixMarker = "###PIX_MARKER###";

  if (children && children.includes("***")) {
    children = children.replace(/\*\*\*/g, pixMarker);
  }

  if (children && boldRegex.test(children)) {
    children = children.replace(boldRegex, "**$1**");
  }

  if (children && children.includes(pixMarker)) {
    children = children.replace(new RegExp(pixMarker, "g"), "***");
  }

  if (children && tildaRegex.test(children)) {
    children = children.replace(tildaRegex, "~~$1~~");
  }
  const options = React.useMemo(() => {
    const markdownOptions = {
      disableParsingRawHTML: true,
      forceInline: true,
      overrides: {
        a: { component: CustomLink },
      },
    };

    elements.forEach((element) => {
      if (!allowedElements.includes(element)) {
        markdownOptions.overrides[element] = (el) => el.children || null;
      }
    });

    return markdownOptions;
  }, []);

  if (!children) return null;

  // Se não há callback de telefone, renderizar normalmente
  if (!onPhoneClick) {
    return <Markdown options={options}>{children}</Markdown>;
  }

  // Parsear telefones no texto
  const segments = parsePhoneNumbers(children);

  // Se não encontrou números, renderizar normalmente
  const hasClickableNumbers = segments.some((s) => s.type === "phone" || s.type === "number");
  if (!hasClickableNumbers) {
    return <Markdown options={options}>{children}</Markdown>;
  }

  // Renderizar com números clicáveis
  return (
    <>
      {segments.map((segment, idx) => {
        if (segment.type === "phone" || segment.type === "number") {
          const isPhone = segment.type === "phone";
          const value = segment.normalized || segment.copyValue || segment.value;

          return (
            <span
              key={idx}
              className="clickable-phone-number"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onPhoneClick(value, e.currentTarget, {
                  isPhone,
                  displayValue: segment.value,
                  copyValue: segment.copyValue || value
                });
              }}
              title={`Opções para ${segment.value}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();
                  onPhoneClick(value, e.currentTarget, {
                    isPhone,
                    displayValue: segment.value,
                    copyValue: segment.copyValue || value
                  });
                }
              }}
            >
              {segment.value}
            </span>
          );
        }
        return (
          <Markdown key={idx} options={options}>
            {segment.value}
          </Markdown>
        );
      })}
    </>
  );
};

export default MarkdownWrapper;
